import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError, fmtDuration, getPlaybackUrl, getDistributionUrl } from "@/lib/api";
import { BrandPreview } from "@/components/BrandPreview";
import { ContentPicker } from "@/components/ContentPicker";
import { getDescriptorFonts, loadDescriptorFont } from "@/lib/descriptorFonts";
import { X, Check, CaretRight, CaretLeft, UploadSimple, Repeat, CalendarBlank, Path, Copy, ArrowSquareOut, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STEPS = ["Basics", "Branding", "Content", "Review"];
const POSITIONS = [
  { v: "top_left", label: "Top Left" }, { v: "top_right", label: "Top Right" },
  { v: "bottom_left", label: "Bottom Left" }, { v: "bottom_right", label: "Bottom Right" },
];
const MODES = [
  { v: "loop", label: "Continuous Loop", desc: "Loops your content 24/7. No scheduling needed.", icon: Repeat, disabled: false },
  { v: "scheduled", label: "Scheduled", desc: "Clock-based scheduling with programming blocks.", icon: CalendarBlank, disabled: false },
  { v: "hybrid", label: "Hybrid", desc: "Coming in a future release.", icon: Path, disabled: true },
];

export function BrandControls({ brand, onChange }) {
  const set = (k, v) => onChange({ ...brand, [k]: v });
  const [fontList, setFontList] = useState([]);
  const descFont = brand.descriptor_font || "system";
  const descSize = brand.descriptor_font_size || "medium";
  const descBg = brand.descriptor_bg_opacity ?? 50;

  useEffect(() => { getDescriptorFonts().then(setFontList); }, []);
  useEffect(() => {
    const f = fontList.find((x) => x.key === descFont);
    if (f?.css) loadDescriptorFont(f.css);   // load the selected family so the preview renders it
  }, [descFont, fontList]);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/(png|svg)/i.test(file.type)) { toast.error("Use a PNG or SVG file"); return; }
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Logo must be under 1.5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => set("logo", reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="space-y-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#525252] font-bold border-b border-[#262626] pb-1.5">Channel Logo</div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Channel Logo (PNG / SVG)</label>
        <div className="flex items-center gap-3">
          <label data-testid="logo-upload-label" className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] hover:border-[#3A86FF] rounded-sm px-3 py-2 text-sm cursor-pointer transition-colors duration-150">
            <UploadSimple size={16} /> Upload
            <input data-testid="logo-upload-input" type="file" accept="image/png,image/svg+xml" onChange={onLogo} className="hidden" />
          </label>
          {brand.logo && <button data-testid="logo-remove" onClick={() => set("logo", null)} className="text-xs text-red-500 hover:underline">Remove</button>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Position</label>
          <select data-testid="logo-position-select" value={brand.logo_position} onChange={(e) => set("logo_position", e.target.value)}
            className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none">
            {POSITIONS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Size</label>
          <select data-testid="logo-size-select" value={brand.logo_size} onChange={(e) => set("logo_size", e.target.value)}
            className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none capitalize">
            <option value="small">Small</option><option value="medium">Medium</option><option value="large">Large</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Opacity — {brand.opacity}%</label>
        <input data-testid="logo-opacity-slider" type="range" min="10" max="100" value={brand.opacity}
          onChange={(e) => set("opacity", parseInt(e.target.value, 10))} className="w-full accent-[#3A86FF]" />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#737373] font-bold">Accent Color</label>
          <input data-testid="accent-color-input" type="color" value={brand.accent_color || "#3A86FF"} onChange={(e) => set("accent_color", e.target.value)}
            className="h-7 w-10 bg-transparent border border-[#262626] rounded-sm cursor-pointer" />
        </div>
        <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer">
          <input data-testid="safe-area-toggle" type="checkbox" checked={brand.safe_area} onChange={(e) => set("safe_area", e.target.checked)} className="accent-[#3A86FF]" />
          Safe area alignment
        </label>
      </div>

      {/* ── Channel Descriptor: how Program Information text looks (channel-wide) ── */}
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#525252] font-bold border-b border-[#262626] pb-1.5 pt-2">Channel Descriptor</div>
      <p className="text-[10px] text-[#525252] leading-snug -mt-2">Controls the appearance of Program Information text. What it says and where it appears stays on each event in Edit Loop Content.</p>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Font Family</label>
        <select data-testid="descriptor-font-select" value={descFont} onChange={(e) => set("descriptor_font", e.target.value)}
          className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none">
          {fontList.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Font Size</label>
          <select data-testid="descriptor-size-select" value={descSize} onChange={(e) => set("descriptor_font_size", e.target.value)}
            className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none capitalize">
            <option value="small">Small</option><option value="medium">Medium</option><option value="large">Large</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Text Background — {descBg}%</label>
          <input data-testid="descriptor-bg-slider" type="range" min="0" max="100" value={descBg}
            onChange={(e) => set("descriptor_bg_opacity", parseInt(e.target.value, 10))} className="w-full accent-[#3A86FF] mt-2" />
        </div>
      </div>
    </div>
  );
}

export function ChannelWizard({ onClose, onCreated }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [pbUrl, setPbUrl] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", resolution: "1080p", aspect_ratio: "16:9", mode: "loop",
    brand: { logo: null, logo_position: "top_right", logo_size: "medium", opacity: 100, safe_area: true, accent_color: "#3A86FF", descriptor_font: "system", descriptor_font_size: "medium", descriptor_bg_opacity: 50 },
    loop_playlist: [],
  });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const runtime = form.loop_playlist.reduce((a, e) => a + (e.duration_seconds || 0), 0);

  const next = () => {
    if (step === 0 && !form.name.trim()) { toast.error("Channel name is required"); return; }
    setStep(Math.min(step + 1, STEPS.length - 1));
  };
  const back = () => setStep(Math.max(step - 1, 0));

  const publish = async () => {
    setSaving(true);
    try {
      const { data: ch } = await api.post("/channels", form);
      const { data: pub } = await api.post(`/channels/${ch.id}/publish`);
      setResult({ channel: ch, ...pub });
      if (pub.published) {
        toast.success("Channel published");
        getPlaybackUrl(ch.id).then(setPbUrl).catch(() => setPbUrl(null));
      }
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#141414] border border-[#262626] rounded-sm w-full max-w-3xl max-h-[90vh] flex flex-col ccs-enter">
        <div className="px-5 py-3 border-b border-[#262626] flex items-center justify-between">
          <h3 className="font-head font-semibold text-sm">Create Channel</h3>
          <button data-testid="wizard-close" onClick={onClose} className="text-[#737373] hover:text-white transition-colors duration-150"><X size={18} /></button>
        </div>

        {result && (
          <div className="p-8 overflow-y-auto flex-1" data-testid="publish-result">
            {result.published ? (
              <div className="text-center">
                <CheckCircle size={44} weight="fill" className="mx-auto mb-3 text-emerald-500" />
                <h3 className="font-head font-bold text-xl">Channel Published Successfully</h3>
                <p className="text-sm text-[#737373] mt-1">{result.channel.name} is now running 24/7.</p>
              </div>
            ) : (
              <div className="text-center">
                <XCircle size={44} weight="fill" className="mx-auto mb-3 text-amber-500" />
                <h3 className="font-head font-bold text-xl">Channel Created as Draft</h3>
                <p className="text-sm text-[#737373] mt-1">Complete the remaining items to publish this channel.</p>
              </div>
            )}

            {!result.published && (
              <div className="mt-5 bg-[#0A0A0A] border border-[#262626] rounded-sm divide-y divide-[#1c1c1c]">
                {result.readiness.checks.map((c) => (
                  <div key={c.key} className="flex items-center gap-2 px-4 py-2 text-sm" data-testid={`result-check-${c.key}`}>
                    {c.ok ? <CheckCircle size={16} className="text-emerald-500" /> : <XCircle size={16} className="text-red-500" />}
                    <span className={c.ok ? "text-[#A3A3A3]" : "text-white"}>{c.label}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Distribution URL (HLS)</div>
              {result.published ? (
                <>
                  <div className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded-sm p-2">
                    <code data-testid="result-distribution-url" className="font-mono text-[10px] text-emerald-400 truncate flex-1">{getDistributionUrl(result.channel.id)}</code>
                    <button data-testid="result-copy-distribution" onClick={() => { navigator.clipboard.writeText(getDistributionUrl(result.channel.id)); toast.success("Distribution URL copied"); }} className="text-[#737373] hover:text-white transition-colors duration-150"><Copy size={14} /></button>
                  </div>
                  <p className="text-[10px] text-[#525252] mt-2">Permanent HLS endpoint — paste into ReVine TV or any compatible player.</p>
                  {pbUrl && (
                    <button data-testid="result-open-live-preview" onClick={() => window.open(pbUrl, "_blank", "noreferrer")}
                      className="inline-flex items-center gap-1.5 text-xs text-[#3A86FF] hover:underline mt-3"><ArrowSquareOut size={13} /> Open Live Preview</button>
                  )}
                </>
              ) : (
                <p data-testid="result-distribution-url-pending" className="text-xs text-[#737373]">The Distribution URL is available once the channel is live.</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 mt-6">
              <button data-testid="result-open-channel" onClick={() => navigate(`/channels/${result.channel.id}`)}
                className="px-4 py-2 text-sm bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 rounded-sm font-medium">Open Channel</button>
              <button data-testid="result-open-outputs" onClick={() => navigate("/outputs")}
                className="px-4 py-2 text-sm border border-[#262626] hover:border-[#3a3a3a] transition-colors duration-150 rounded-sm">Open Outputs</button>
              <button data-testid="result-open-monitoring" onClick={() => navigate("/monitoring")}
                className="px-4 py-2 text-sm border border-[#262626] hover:border-[#3a3a3a] transition-colors duration-150 rounded-sm">Open Monitoring</button>
              <button data-testid="result-dashboard" onClick={() => navigate("/")}
                className="px-4 py-2 text-sm border border-[#262626] hover:border-[#3a3a3a] transition-colors duration-150 rounded-sm">Return to Dashboard</button>
            </div>
          </div>
        )}

        {!result && (<>
        <div className="flex items-center gap-1 px-5 py-3 border-b border-[#262626]">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1 flex-1">
              <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-mono shrink-0 ${
                i < step ? "bg-emerald-500 text-black" : i === step ? "bg-[#3A86FF] text-white" : "bg-[#0A0A0A] border border-[#262626] text-[#525252]"}`}>
                {i < step ? <Check size={12} weight="bold" /> : i + 1}
              </div>
              <span className={`text-xs ${i === step ? "text-white" : "text-[#525252]"}`}>{s}</span>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-[#262626]" />}
            </div>
          ))}
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {step === 0 && (
            <div className="space-y-4">
              <Field label="Channel name" testid="wizard-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Description</label>
                <textarea data-testid="wizard-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2}
                  className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none transition-colors duration-150" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Resolution</label>
                  <select data-testid="wizard-resolution" value={form.resolution} onChange={(e) => set("resolution", e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none">
                    <option value="1080p">1080p — Full HD</option><option value="720p">720p — HD</option><option value="480p">480p — SD</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Aspect Ratio</label>
                  <select data-testid="wizard-aspect" value={form.aspect_ratio} onChange={(e) => set("aspect_ratio", e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none">
                    <option value="16:9">16:9 — Widescreen</option><option value="4:3">4:3 — Standard</option><option value="9:16">9:16 — Vertical</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-2 font-bold">Channel Mode</label>
                <div className="grid grid-cols-1 gap-2">
                  {MODES.map((m) => (
                    <button key={m.v} data-testid={`wizard-mode-${m.v}`} disabled={m.disabled}
                      onClick={() => !m.disabled && set("mode", m.v)}
                      className={`flex items-center gap-3 p-3 rounded-sm border text-left transition-colors duration-150 ${
                        form.mode === m.v ? "border-[#3A86FF] bg-[#3A86FF]/10" : "border-[#262626] hover:border-[#3a3a3a]"} ${m.disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
                      <m.icon size={20} className={form.mode === m.v ? "text-[#3A86FF]" : "text-[#737373]"} />
                      <div>
                        <div className="text-sm font-medium flex items-center gap-2">{m.label}{m.v === "loop" && <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">Default</span>}</div>
                        <div className="text-xs text-[#737373]">{m.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <BrandControls brand={form.brand} onChange={(b) => set("brand", b)} />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Live Preview</label>
                <BrandPreview brand={form.brand} />
              </div>
            </div>
          )}

          {step === 2 && (
            form.mode === "loop" ? (
              <ContentPicker value={form.loop_playlist} onChange={(v) => set("loop_playlist", v)} />
            ) : (
              <div className="text-center py-16 px-6">
                <CalendarBlank size={36} className="mx-auto mb-3 text-[#3A86FF]" />
                <h4 className="font-head font-semibold text-base mb-1">Scheduled programming</h4>
                <p className="text-sm text-[#737373] max-w-md mx-auto">
                  This channel uses clock-based scheduling. After publishing, build Programming Blocks and arrange them on the Scheduler timeline. No content selection is needed here.
                </p>
              </div>
            )
          )}

          {step === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="font-head font-semibold text-sm">Channel Summary</h4>
                <Row label="Name" value={form.name} />
                <Row label="Mode" value={form.mode === "loop" ? "Continuous Loop" : "Scheduled"} />
                <Row label="Resolution" value={`${form.resolution} · ${form.aspect_ratio}`} />
                <Row label="Estimated Runtime" value={form.mode === "loop" ? (runtime ? fmtDuration(runtime) + " loop" : "No content yet") : "Configured in Scheduler"} />
                <Row label="Content Items" value={form.mode === "loop" ? String(form.loop_playlist.length) : "—"} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Branding Preview</label>
                <BrandPreview brand={form.brand} />
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#262626] flex justify-between">
          <button data-testid="wizard-back" onClick={back} disabled={step === 0}
            className="flex items-center gap-1 px-4 py-2 text-sm text-[#A3A3A3] hover:text-white disabled:opacity-30 transition-colors duration-150">
            <CaretLeft size={14} /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button data-testid="wizard-next" onClick={next}
              className="flex items-center gap-1 px-4 py-2 text-sm bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 rounded-sm font-medium">
              Next <CaretRight size={14} />
            </button>
          ) : (
            <button data-testid="wizard-publish" onClick={publish} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 transition-colors duration-150 rounded-sm font-medium disabled:opacity-60">
              <Check size={16} weight="bold" /> {saving ? "Publishing…" : "Publish Channel"}
            </button>
          )}
        </div>
        </>)}
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">{label}</label>
      <input data-testid={testid} {...props}
        className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2.5 text-sm outline-none transition-colors duration-150" />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-[#1c1c1c] py-2">
      <span className="text-xs text-[#737373]">{label}</span>
      <span className="text-sm text-right">{value}</span>
    </div>
  );
}
